import TableOnly from "components/DataTable/TableOnly";
import Pagination from "components/Pagination/Pagination";
import SearchComp from "components/SearchComp/SearchComp";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import { parseFloatData } from "utils/UtilityFuncs";
import DeleteModalButton from "../../../../components/DeleteModalBtn/DeleteModalButton";
import PurchaseIssueHistory from "./PurchaseIssueHistory";
import PurchaseZplButton from "./PurchaseZplButton";
import SelectMaterialList from "./SelectMaterialList";

let API_URL = "/manufacturer-purchases/";
let COMPONENT_URL = "/manufacturer/material-stock";

let limits = [1, 5, 10, 15, 20];

export default function Purchases() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  /*   ALL STATES
   ********************************************* */
  const [purchaseData, setPurchaseData] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showSellModal, setShowSellModal] = useState(false);

  const [formData, setFormData] = useState({
    purchaseDate: new Date().toISOString().slice(0, 10),
  });
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  const [loading, setLoading] = useState(false);

  const [materialListData, setMaterialListData] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [vendorsList, setVendorsList] = useState(null);
  const [isReturn, setIsReturn] = useState(false);

  const [searchResults, setSearchResults] = useState(null);
  const [showMaterialModal, setShowMaterialModal] = useState(false);

  const [totalAvailableQty, setTotalAvailableQty] = useState("N/A");

  const [totalPages, setTotalPages] = useState(
    parseInt(searchParams.get("totalPages")) || 1
  );

  if (searchParams.get("limit") === null) {
    setSearchParams((prev) => {
      let limit = searchParams.get("limit") || "10";
      const newParams = new URLSearchParams(prev);
      newParams.set("limit", limit);
      return newParams.toString();
    });
  }

  if (searchParams.get("page") === null) {
    setSearchParams((prev) => {
      let page = searchParams.get("page") || "1";
      const newParams = new URLSearchParams(prev);
      newParams.set("page", page);
      return newParams.toString();
    });
  }

  if (searchParams.get("filterBy") === null) {
    setSearchParams((prev) => {
      let filterBy = searchParams.get("filterBy") || "all";
      const newParams = new URLSearchParams(prev);
      newParams.set("filterBy", filterBy);
      return newParams.toString();
    });
  }

  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear() - 1, now.getMonth() - 1, 2)
      .toISOString()
      .slice(0, 10)
      .replace("T", " ");
  });

  const [endDate, setEndDate] = useState(
    new Date().toISOString().slice(0, 10).replace("T", " ")
  );

  const [showDateFilter, setShowDateFilter] = useState(false);

  /*   All Functions
   ********************************************* */
  const changeLimit = (val) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("limit", val);
      return newParams.toString();
    });
  };

  const getSearchParamsWithoutGivenKey = (key) => {
    let params = new URLSearchParams(searchParams);
    params.delete(key);

    return params.toString();
  };

  //get Ext Transaction Data
  const getPurchaseData = async () => {
    try {
      setLoading(true);
      // increment 1 to endDate
      const endDateObj = new Date(endDate);
      // get only date frm endDateObj not time
      const endDateString = endDateObj.toISOString().slice(0, 10);

      if (!selectedMaterial?.id) {
        setPurchaseData([]);
        return;
      }

      let page = parseInt(searchParams.get("page")) || 1;
      let limit = parseInt(searchParams.get("limit")) || 10;
      let query = searchParams.get("query") || "";
      let filterBy = searchParams.get("filterBy") || "all";

      let body = { page, limit, filterBy };

      // check for sort and order in searchParams
      let sort = searchParams.get("sort");
      let order = searchParams.get("order");
      if (sort && order) {
        body.sort = sort;
        body.order = order;
      }

      if (query) {
        body.query = query;
      }

      if (startDate) {
        body.startDate = startDate;
      }
      if (endDate) {
        body.endDate = endDateString;
      }

      const { data } = await API.post(
        API_URL + selectedMaterial?.materialId,
        body,
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        setPurchaseData(data?.data);
        setTotalAvailableQty(data?.totalAvailableQty || "N/A");
        setTotalPages(data.totalPages);
        setSearchResults(null);
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  //get Vendors List
  const getVendorsList = async () => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/manufacturer-vendors",
        { filterBy: "all", limit: 1000000, page: 1 },
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        setVendorsList(data?.data);
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  // Delete
  const deletePurchaseData = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete("/manufacturer-purchase/" + id, {
        headers: authHeader(),
      });
      if (data?.success) {
        setPurchaseData((prev) => prev.filter((item) => item.id !== id));
        dispatch(
          setNotification({
            message: data?.message,
            type: "success",
          })
        );
        return;
      }
      dispatch(
        setNotification({
          message: data?.message,
          type: "error",
        })
      );
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      reset();
    }
  };

  // Adds Button Function
  const AddPurchaseData = async (e) => {
    e.preventDefault();

    try {
      if (!selectedMaterial?.id) {
        dispatch(
          setNotification({
            message: "Please select a material",
            type: "error",
          })
        );
        return;
      }
      if (!formData.vendorId) {
        dispatch(
          setNotification({
            message: "Please select a vendor",
            type: "error",
          })
        );
        return;
      }
      let body = {
        ...formData,
        materialId: selectedMaterial?.materialId,
        styleId: selectedMaterial?.styleId,
      };

      // get selected vendor name
      const selectedVendor = vendorsList?.find(
        (vendor) => vendor.id === formData.vendorId
      );

      if (selectedVendor) {
        body.vendorName = selectedVendor.name;
        body.vendorId = selectedVendor.vendorId;
      }

      setLoading(true);
      const { data } = await API.post("/manufacturer-purchase", body, {
        headers: authHeader(),
      });
      if (data?.success) {
        // setPurchaseData((prev) => [...prev, data?.data]);
        await getPurchaseData();
        setFormData({});
        setShowAddModal(false);
      } else {
        dispatch(
          setNotification({
            message: data?.message,
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      reset();
    }

    setShowAddModal(false);
  };

  // Update Button Function
  const UpdatePurchaseData = async (e) => {
    e.preventDefault();
    try {
      if (!selectedMaterial?.id) {
        dispatch(
          setNotification({
            message: "Please select a material",
            type: "error",
          })
        );
        return;
      }
      if (!formData.vendorId) {
        dispatch(
          setNotification({
            message: "Please select a vendor",
            type: "error",
          })
        );
        return;
      }

      let body = {
        // ...formData,

        id: formData.id,
        styleId: formData?.styleId,
        purchaseId: formData.purchaseId,

        purchaseDate: formData.purchaseDate,
        materialId: formData?.materialId,
        vendorId: formData.vendorId,
        vendorName: formData.vendorName,
        qty: formData.qty,
        // receivedQty: formData.receivedQty,
        purchasedQty: formData.purchasedQty,
        unitPrice: formData.unitPrice,
        location: formData.location,
        poNumber: formData.poNumber,

        isReturn: isReturn,
        lotTotalPrice: formData.lotTotalPrice,
        soldQty: formData.soldQty || 0,
        sellDate: formData.sellDate || null,
        returnQty: formData.returnQty || 0,
      };

      // console.log(body);
      // return;

      setLoading(true);
      const { data } = await API.patch("/manufacturer-purchase/", body, {
        headers: authHeader(),
      });
      if (data?.success) {
        setPurchaseData((prev) => {
          const newData = prev.map((item) => {
            if (item.id === formData.id) {
              return data?.data;
            }
            return item;
          });
          return newData;
        });
      } else {
        dispatch(
          setNotification({
            message: data?.message,
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      await getPurchaseData();
      reset();
    }
  };

  // Sell Button Function
  const SellPurchaseData = async (e) => {
    e.preventDefault();
    try {
      if (!selectedMaterial?.id) {
        dispatch(
          setNotification({
            message: "Please select a material",
            type: "error",
          })
        );
        return;
      }

      // Allow zero for resetting sold quantity
      if (formData.sellQty === undefined || formData.sellQty === "") {
        dispatch(
          setNotification({
            message: "Please enter a valid sell quantity",
            type: "error",
          })
        );
        return;
      }

      const sellQtyValue = parseFloatData(formData.sellQty);
      if (sellQtyValue < 0) {
        dispatch(
          setNotification({
            message: "Sell quantity cannot be negative",
            type: "error",
          })
        );
        return;
      }

      if (
        sellQtyValue > getTotalAvailableForSell(formData) &&
        sellQtyValue !== 0
      ) {
        dispatch(
          setNotification({
            message: "Sell quantity cannot exceed total available quantity",
            type: "error",
          })
        );
        return;
      }
      let body = {
        id: formData.id,
        newAvailableQty:
          sellQtyValue === 0
            ? formData.qty + (formData.soldQty || 0) // Reset sold qty back to available
            : formData.qty, // Normal sell
        soldQty: sellQtyValue,
        resetSold: sellQtyValue === 0,
        sellDate: formData.sellDate,
        previousSoldQty: formData.soldQty || 0,
      };
      setLoading(true);
      const { data } = await API.post("/manufacturer-sell-stock", body, {
        headers: authHeader(),
      });
      if (data?.success) {
        setPurchaseData((prev) => {
          const newData = prev.map((item) => {
            if (item.id === formData.id) {
              return {
                ...item,
                qty:
                  sellQtyValue === 0
                    ? item.qty + (item.soldQty || 0)
                    : item.qty - sellQtyValue,
                soldQty: sellQtyValue === 0 ? 0 : sellQtyValue,
              };
            }
            return item;
          });
          return newData;
        });
        dispatch(
          setNotification({
            message:
              data?.message ||
              (sellQtyValue === 0
                ? "Sold quantity reset successfully"
                : "Stock sold successfully"),
            type: "success",
          })
        );
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to sell stock",
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      await getPurchaseData();
      reset();
    }
  };

  const handleUpdateData = async (item) => {
    setFormData(item);
    setIsUpdateMode(true);
    setShowAddModal(true);
  };

  const handleReturnData = async (item) => {
    setFormData(item);
    setIsUpdateMode(true);
    setShowReturnModal(true);
  };

  const handleSellData = async (item) => {
    // Set initial sell quantity to previous sold quantity if it exists
    const initialData = {
      ...item,
      sellQty: item.soldQty || 0,
    };
    setFormData(initialData);
    setIsUpdateMode(true);
    setShowSellModal(true);
  };

  // get total issue qty from purchase order

  const getTotalIssueQty = (purchaseOrder) => {
    if (!purchaseOrder?.m_issues || purchaseOrder?.m_issues?.length === 0) {
      return 0;
    }

    const result = purchaseOrder?.m_issues?.reduce((total, issue) => {
      return total + (issue?.qty || 0);
    }, 0);
    return parseFloatData(result) || 0;
  };

  const getMaxReturnQty = (purchaseOrder) => {
    if (!purchaseOrder?.m_issues || purchaseOrder?.m_issues?.length === 0) {
      return purchaseOrder?.purchasedQty || 0;
    }
    return (
      purchaseOrder?.purchasedQty -
      (purchaseOrder?.returnQty || 0) -
      (purchaseOrder?.soldQty || 0) -
      getTotalIssueQty(purchaseOrder)
    );
  };

  const getTotalAvailableForSell = (purchaseOrder) => {
    if (!purchaseOrder?.m_issues || purchaseOrder?.m_issues?.length === 0) {
      return purchaseOrder?.purchasedQty || 0;
    }
    return (
      purchaseOrder?.purchasedQty -
      (purchaseOrder?.returnQty || 0) -
      getTotalIssueQty(purchaseOrder)
    );
  };

  const reset = () => {
    setFormData({});
    setLoading(false);
    setIsUpdateMode(false);
    setShowReturnModal(false);
    setIsReturn(false);
    setShowAddModal(false);
    setShowSellModal(false);
  };

  const fetchMaterialListData = async () => {
    try {
      setLoading(true);

      const { data } = await API.post(
        "/manufacturer/material-lists/",
        { filterBy: "all", limit: 1000000, page: 1 },
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        setMaterialListData(data?.data);
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  // Getting 12 Hours Time from SQL timestamp
  const convertSqlTimestampToDate = (sqlTime) => {
    const timestamp = sqlTime;
    const date = new Date(timestamp);
    const time = date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    return time;
  };

  /*   All Use Effects
   ********************************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      let _filterBy = searchParams.get("filterBy") || "all";
      let _query = searchParams.get("query") || "";
      if (_filterBy && _filterBy !== "all" && !_query) {
        return;
      }

      if (selectedMaterial?.id) {
        setFormData({
          ...formData,
          materialId: selectedMaterial?.materialId,
          styleId: selectedMaterial?.styleId,
        });
        getPurchaseData();
      } else {
        setPurchaseData([]);
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedMaterial, searchParams]);

  useEffect(() => {
    let timeout = setTimeout(() => {
      getVendorsList();
      fetchMaterialListData();
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    let timeout = setTimeout(() => {
      let getMaterialId = searchParams.get("materialId");

      if (!selectedMaterial?.id && getMaterialId) {
        async function fetchData() {
          try {
            setLoading(true);

            const { data } = await API.post(
              "manufacturer-material-lists/" + getMaterialId,
              { filterBy: "all" },
              {
                headers: authHeader(),
              }
            );
            if (data?.success) {
              setSelectedMaterial(data?.data);
            }
          } catch (err) {
            dispatch(
              setNotification({
                message: err.message,
                type: "error",
              })
            );
          } finally {
            setLoading(false);
          }
        }
        fetchData();
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedMaterial, searchParams]);

  /*   Table functions
   ********************************************* */
  const btn1 = () => (
    <div>
      <>
        <button
          onClick={() => {
            setShowAddModal(true);
            setFormData({
              purchaseDate: new Date().toISOString().slice(0, 10),
              location: "Store",
            });
            setIsUpdateMode(false);
          }}
          disabled={loading || !selectedMaterial?.id}
          type="button"
          className="btn btn-primary  "
        >
          Purchase
        </button>
      </>
    </div>
  );

  const table_head = [
    { name: "T-ID", value: "tid" },
    { name: "Lot ID", value: "lotId" },
    { name: "Style ID", value: "styleId" },
    { name: "Color", value: "m_materialList.color" },
    { name: "Vendor", value: "m_vendor.name" },
    { name: "Original Qty", value: "purchasedQty" },
    { name: "Return Qty", value: "returnQty" },
    { name: "Sold Qty", value: "soldQty" },
    { name: "Available Qty", value: "qty" },
    { name: "Created At", value: "createdAt" },

    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item, index) => (
    <tr
      className={`${
        searchParams?.get("tid") === item?.tid?.toString()
          ? "bg-primary bg-opacity-25"
          : ""
      }`}
      key={item.id}
    >
      <td>{item?.tid}</td>
      <td>{item?.lotId}</td>
      <td>{item?.styleId}</td>
      <td>{item?.m_materialList?.color}</td>
      <td>{item?.m_vendor?.name}</td>
      <td className="">{item?.purchasedQty}</td>
      <td className="">{item?.returnQty || "-"}</td>
      <td className="">{item?.soldQty || "-"}</td>
      <td className="">{item?.qty}</td>
      <td>{convertSqlTimestampToDate(item?.createdAt)}</td>

      <td className="d-flex align-items-center justify-content-end gap-1 p-1 ">
        {!item?.returnQty && (
          <button
            disabled={loading}
            onClick={() => {
              handleUpdateData(item);
            }}
            className="btn px-1"
          >
            <i className="mdi mdi-pencil"></i>
          </button>
        )}

        <DeleteModalButton
          btnClass={"btn  px-1"}
          deleteFunc={deletePurchaseData}
          deleteParams={item.id}
          disableConditions={loading || item?.m_issues?.length > 0}
        />

        <PurchaseZplButton
          labelData={[
            {
              row: 1,
              column: "Name",
              value: item?.styleId,
            },
            {
              row: 2,
              column: "Color",
              value: item?.m_materialList?.color || "N/A",
            },
            {
              row: 3,
              column: "Vendor",
              value: item?.m_vendor?.name || "N/A",
            },
            {
              row: 4,
              column: "Lot ID",
              value: item?.lotId || "N/A",
            },
            {
              row: 5,
              column: "Qty",
              value: parseFloatData(item?.qty), // up to 3 decimal places
            },
            {
              row: 6,
              column: "Date",
              value: convertSqlTimestampToDate(item?.createdAt) || "N/A",
            },
          ]}
        />
        <PurchaseIssueHistory purchaseOrder={item} />
        <button
          disabled={loading}
          onClick={() => {
            setIsReturn(true);
            handleReturnData(item);
          }}
          className="btn btn-warning btn-sm text-black"
        >
          Return
        </button>
        <button
          disabled={loading}
          onClick={() => {
            handleSellData(item);
          }}
          className="btn btn-success btn-sm"
        >
          Sell
        </button>
      </td>
    </tr>
  );

  return (
    <div className="">
      {loading && <Spinner />}
      {/*   Table
       ********************************************* */}
      <div className="pe-2 flex-grow-1 d-flex gap-3 ">
        <div className="w-100">
          {/* Table */}
          <>
            <div className="">
              <div className="d-flex justify-content-end align-items-center mb-3">
                <div className="flex-grow-1 d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-1">
                    <div>Show</div>
                    <button
                      className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {searchParams.get("limit")}
                    </button>
                    <ul
                      style={{ width: "fit-content" }}
                      className="dropdown-menu"
                    >
                      {limits.map((limit) => (
                        <li key={limit}>
                          <button
                            className="dropdown-item"
                            onClick={() => changeLimit(limit)}
                          >
                            {limit}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="d-flex align-items-center">
                    <div className="d-flex align-items-center gap-2 me-4">
                      <h5 className="me-1">
                        Selected Material: {selectedMaterial?.styleId || "N/A"}
                      </h5>
                      <button
                        onClick={() => setShowMaterialModal(true)}
                        className="btn btn-primary"
                      >
                        {selectedMaterial
                          ? "Change Material"
                          : "Select Material"}
                      </button>
                    </div>
                    {/* Filter By Date */}
                    <div
                      className="d-flex align-items-center "
                      style={{ minWidth: 250 }}
                    >
                      {/* From */}
                      <div className="row ">
                        <div className="col-3  d-flex justify-content-start ms-1 align-items-center ">
                          <div className="">From</div>
                        </div>
                        <div className="col-8 px-0">
                          <input
                            className="form-control"
                            value={startDate || ""}
                            onChange={(e) => setStartDate(e.target.value)}
                            type={"date"}
                          />
                        </div>
                      </div>
                      {/* To */}
                      <div className="row">
                        <div className="col-3  d-flex justify-content-start ms-1 align-items-center ">
                          <div className="">TO</div>
                        </div>
                        <div className="col-8 px-0">
                          <input
                            className="form-control"
                            value={endDate || ""}
                            onChange={(e) => setEndDate(e.target.value)}
                            type={"date"}
                          />
                        </div>
                      </div>
                      <div className="d-flex justify-content-end ">
                        <button
                          onClick={() => {
                            getPurchaseData();
                            setShowDateFilter(!showDateFilter);
                          }}
                          className="btn btn-success mx-2"
                        >
                          FILTER
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <SearchComp
                  setLoading={setLoading}
                  apiPath={`${COMPONENT_URL}${
                    getSearchParamsWithoutGivenKey("query")
                      ? "?" + getSearchParamsWithoutGivenKey("query") + "&"
                      : "?"
                  }`}
                />
                <div className="ms-1">{btn1()}</div>
              </div>
              <div
                style={{
                  height: "63vh",
                  overflowY: "auto",
                }}
              >
                <TableOnly
                  table_head={table_head}
                  table_body={table_body}
                  table_data={searchResults ? searchResults : purchaseData}
                  totalPages={totalPages}
                  disableSorting={true}
                />
              </div>

              {loading && <Spinner />}
              {!searchResults && (
                <Pagination
                  totalPages={totalPages}
                  apiPath={`${COMPONENT_URL}${
                    searchParams.toString()
                      ? "?" + getSearchParamsWithoutGivenKey("page") + "&"
                      : "?"
                  }page=`}
                  currPage={parseInt(searchParams.get("page")) || 1}
                />
              )}
            </div>
            <hr className="mt-0" />
            <div className="text-end">
              <h4 className="text-danger mb-0 fw-bold">
                Total Available Qty: &nbsp;
                {totalAvailableQty !== "N/A"
                  ? parseFloat(totalAvailableQty).toFixed(2)
                  : "N/A"}
              </h4>
            </div>
          </>
        </div>
      </div>
      {/*   ADD Modal
       ********************************************* */}
      <Modal show={showAddModal} onHide={() => reset()}>
        <Modal.Body
          className="shadow-lg bg-white px-3 "
          style={{ borderRadius: 15 }}
        >
          <div className="">
            <h4>{isUpdateMode ? "Update Purchase" : "Add Purchase"} </h4>
          </div>
          <hr className="mt-0" />
          <form
            onSubmit={isUpdateMode ? UpdatePurchaseData : AddPurchaseData}
            className=""
          >
            {/* Purchase Date */}
            <div className="mb-3">
              <label htmlFor="purchaseDate" className="form-label">
                Purchase Date
              </label>
              <input
                type="date"
                className="form-control"
                id="purchaseDate"
                value={formData.purchaseDate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    purchaseDate: e.target.value,
                  })
                }
                required
              />
            </div>

            <div className="mb-3">
              <label htmlFor="materialId" className="form-label">
                Material
              </label>
              <select
                id="materialId"
                className="form-select"
                value={
                  formData.materialId
                    ? formData.materialId
                    : selectedMaterial?.materialId
                }
                onChange={(e) => {
                  let _selectedMaterial = materialListData?.find(
                    (material) =>
                      material.materialId === parseInt(e.target.value)
                  );
                  setSelectedMaterial(_selectedMaterial);
                  if (_selectedMaterial) {
                    setFormData({
                      ...formData,
                      materialId: _selectedMaterial.materialId,
                      styleId: _selectedMaterial.styleId,
                    });
                  }
                }}
                required
              >
                <option value="">Select Material</option>
                {materialListData?.map((material) => (
                  <option key={material.materialId} value={material.materialId}>
                    {material?.styleId +
                      " - " +
                      material?.description +
                      " - GSM " +
                      material?.gsm +
                      " - " +
                      material?.color}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label htmlFor="materialId" className="form-label">
                Vendor
              </label>
              <select
                id="vendorId"
                className="form-select"
                value={formData.vendorId || ""}
                onChange={(e) => {
                  let _vendorId = parseInt(e.target.value);
                  let _vendorName =
                    vendorsList.find((vendor) => vendor.vendorId === _vendorId)
                      ?.name || "";
                  setFormData({
                    ...formData,
                    vendorId: _vendorId,
                    vendorName: _vendorName,
                  });
                }}
                required
              >
                <option value="">Select Vendor</option>
                {vendorsList?.map((vendor) => (
                  <option key={vendor.id} value={vendor.vendorId}>
                    {vendor.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label htmlFor="qty" className="form-label">
                Purchased Quantity (Available: {formData?.qty || 0} ) (Issue
                Qty: {getTotalIssueQty(formData)})
              </label>
              <input
                type="number"
                step="0.001"
                className="form-control"
                id="qty"
                min={getTotalIssueQty(formData)}
                value={formData?.purchasedQty || ""}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    purchasedQty: parseFloatData(e.target.value),
                    qty:
                      parseFloatData(e.target.value) -
                      getTotalIssueQty(formData),
                    lotTotalPrice:
                      formData?.unitPrice * parseFloatData(e.target.value) ||
                      0.0,
                  });
                }}
                placeholder="0.000"
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="unitPrice" className="form-label">
                Unit Price ( Lot Price :{" "}
                {formData?.unitPrice && formData?.purchasedQty
                  ? (formData?.unitPrice * formData?.purchasedQty).toFixed(2)
                  : "0.000"}{" "}
                )
              </label>
              <input
                type="number"
                className="form-control"
                id="unitPrice"
                step="0.01"
                min="0"
                disabled={!formData.purchasedQty || ""}
                value={formData.unitPrice || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    unitPrice: parseFloatData(e.target.value, 2),
                    lotTotalPrice:
                      e.target.value * formData.purchasedQty || 0.0,
                  })
                }
                placeholder="0.00"
                required
              />
            </div>

            <div className="mb-3">
              <label htmlFor="location" className="form-label">
                Location
              </label>
              <input
                type="text"
                className="form-control"
                id="location"
                value={formData.location || ""}
                onChange={(e) =>
                  setFormData({ ...formData, location: e.target.value })
                }
                placeholder="Enter Location"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="poNumber" className="form-label">
                PO/Bill Number
              </label>
              <input
                type="text"
                className="form-control"
                id="poNumber"
                value={formData.poNumber || ""}
                onChange={(e) =>
                  setFormData({ ...formData, poNumber: e.target.value })
                }
                placeholder="Enter PO Number"
              />
            </div>

            {/*   buttons
             ********************************************* */}
            <div className="mt-3">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    reset();
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>
                <button
                  disabled={loading}
                  className="btn btn-primary btn-sm ms-1"
                  type="submit"
                >
                  {isUpdateMode ? "Update" : "Add"}
                </button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
      {/*   Return Modal
       ********************************************* */}
      <Modal show={showReturnModal} onHide={() => setShowReturnModal(false)}>
        <Modal.Body
          className="shadow-lg bg-white px-3 "
          style={{ borderRadius: 15 }}
        >
          <div className="">
            <h4>Return Material</h4>
          </div>
          <hr className="mt-0" />
          <form
            onSubmit={isUpdateMode ? UpdatePurchaseData : AddPurchaseData}
            className=""
          >
            <div className="mb-3">
              <label htmlFor="qty" className="form-label">
                Return Qty ( Can be returned: {getMaxReturnQty(formData)} )
              </label>
              <input
                type="number"
                step="0.001"
                className="form-control"
                id="returnQty"
                min={0}
                max={
                  formData.purchasedQty -
                  getTotalIssueQty(formData) -
                  (formData.soldQty || 0)
                }
                disabled={loading}
                value={formData.returnQty || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    returnQty: parseFloatData(e.target.value),
                    qty:
                      formData.purchasedQty -
                      parseFloatData(e.target.value) -
                      (formData.soldQty || 0),
                    lotTotalPrice:
                      formData.unitPrice * parseFloatData(e.target.value, 2) ||
                      0.0,
                    returnDate: new Date()
                      .toISOString()
                      .slice(0, 19)
                      .replace("T", " "),
                  })
                }
                placeholder="0.000"
                required
              />
            </div>
            <h4 className="mb-0 text-muted">Summary</h4>
            <hr className="mt-0" />

            {/*   Summary
             ********************************************* */}

            {/* Purchased Qty */}
            <div className="row mb-1">
              <div className="col-4 fs-5 fw-bolder">Purchased Qty</div>
              <div className="col-8">
                <span>{formData.purchasedQty || 0.0}</span>
              </div>
            </div>
            {/* Total Issue Qty */}
            <div className="row mb-1">
              <div className="col-4 fs-5 fw-bolder">Total Issue Qty</div>
              <div className="col-8">
                <span>{getTotalIssueQty(formData) || 0.0}</span>
              </div>
            </div>

            {/* Sold */}
            <div className="row mb-1">
              <div className="col-4 fs-5 fw-bolder">Sold Qty</div>
              <div className="col-8">
                <span>{formData.soldQty || 0.0}</span>
              </div>
            </div>
            {/* Available */}
            <div className="row mb-1">
              <div className="col-4 fs-5 fw-bolder">Available Qty</div>
              <div className="col-8">
                <span>{getMaxReturnQty(formData) || 0.0}</span>
              </div>
            </div>

            {/*   buttons
             ********************************************* */}
            <div className="mt-3">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    reset();
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>
                <button
                  disabled={loading}
                  className="btn btn-primary btn-sm ms-1"
                  type="submit"
                >
                  Update
                </button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
      {/*   Sell Modal
       ********************************************* */}
      <Modal show={showSellModal} onHide={() => setShowSellModal(false)}>
        <Modal.Body
          className="shadow-lg bg-white px-3 "
          style={{ borderRadius: 15 }}
        >
          <div className="">
            <h4>Sell Material</h4>
          </div>
          <hr className="mt-0" />
          <form onSubmit={SellPurchaseData} className="">
            {" "}
            <div className="mb-3">
              <label htmlFor="qty" className="form-label">
                Sell Qty ( Can be returned: {getTotalAvailableForSell(formData)}{" "}
                )
              </label>
              <input
                type="number"
                step="0.001"
                className="form-control"
                id="sellQty"
                min={0}
                max={getTotalAvailableForSell(formData)}
                disabled={loading}
                value={formData.sellQty || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    sellQty: parseFloatData(e.target.value),
                    qty:
                      getTotalAvailableForSell(formData) -
                      parseFloatData(e.target.value),
                    sellDate: new Date()
                      .toISOString()
                      .slice(0, 19)
                      .replace("T", " "),
                  })
                }
                placeholder="0.000"
                required
              />
              {formData.soldQty > 0 && (
                <small className="text-info">
                  Enter 0 to reset previously sold quantity
                </small>
              )}
            </div>
            <h4 className="mb-0 text-muted">Summary</h4>
            <hr className="mt-0" />
            {/*   Summary
             ********************************************* */}{" "}
            {/* Purchased Qty */}
            <div className="row mb-1">
              <div className="col-5 fs-5 fw-bolder">Purchased Qty</div>
              <div className="col-7">
                <span>{formData.purchasedQty || 0.0}</span>
              </div>
            </div>
            {/* Total Issue Qty */}
            <div className="row mb-1">
              <div className="col-5 fs-5 fw-bolder">Total Issue Qty</div>
              <div className="col-7">
                <span>{getTotalIssueQty(formData) || 0.0}</span>
              </div>
            </div>
            {/* Total Available */}
            <div className="row mb-1">
              <div className="col-5 fs-5 fw-bolder">Total Available Qty</div>
              <div className="col-7">
                <span>{getTotalAvailableForSell(formData) || 0.0}</span>
              </div>
            </div>
            {/* Current Available */}
            <div className="row mb-1">
              <div className="col-5 fs-5 fw-bolder">Current Available</div>
              <div className="col-7">
                <span>{formData.qty || 0.0}</span>
              </div>
            </div>
            {/* Previously Sold */}
            {formData.soldQty > 0 && (
              <div className="row mb-1">
                <div className="col-5 fs-5 fw-bolder">Previously Sold</div>
                <div className="col-7">
                  <span>{formData.soldQty || 0.0}</span>
                </div>
              </div>
            )}
            {/*   buttons
             ********************************************* */}
            <div className="mt-3">
              <div className="d-flex justify-content-end">
                <button
                  onClick={() => {
                    reset();
                  }}
                  className="btn btn-secondary btn-sm"
                  type="button"
                >
                  Close
                </button>
                <button
                  disabled={loading}
                  className="btn btn-primary btn-sm ms-1"
                  type="submit"
                >
                  Sell
                </button>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
      {/*   Change Material Modal
       ********************************************* */}
      <Modal
        size="lg"
        show={showMaterialModal}
        onHide={() => setShowMaterialModal(false)}
        contentClassName="bg-transparent"
      >
        <Modal.Body className="bg-white px-3 pb-4 custom-shadow-sm rounded-3">
          <div className="d-flex justify-content-between align-items-center">
            <h3>Change Material</h3>
            <button
              onClick={() => {
                setShowMaterialModal(false);
              }}
              className="btn btn-secondary btn-sm"
            >
              Close
            </button>
          </div>
          <hr className="mt-0 mb-3" />
          <SelectMaterialList
            selectedMaterial={selectedMaterial}
            setSelectedMaterial={setSelectedMaterial}
            materialListData={materialListData}
            setMaterialListData={setMaterialListData}
            showModal={setShowMaterialModal}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}
